import { toast, ToastOptions } from "react-toastify"
import { AlertTriangle } from "lucide-react"

import CheckCircleIcon from "@/assets/icons/checkCircle/filled/medium.svg?react"
import CloseIcon from "@/assets/icons/close/bold.svg?react"
import InfoIcon from "@/assets/icons/info/filled/bold.svg?react"
import { cn } from "@/lib/utils"

type ToastType = "success" | "error" | "info" | "warning"

interface CustomToastProps {
  type: ToastType
  title?: string
  message: string
  className?: string
  options?: Partial<ToastOptions>
  closeToast?: () => void
}

const toastConfig: Record<
  ToastType,
  {
    icon: JSX.Element
    containerClasses: string
    color: string
    secondaryBG: string
  }
> = {
  success: {
    icon: <CheckCircleIcon className="h-5 w-5" />,
    containerClasses: "border-2 border-safe-secondBg bg-safe-mainBg",
    color: "text-safe-primary",
    secondaryBG: "bg-safe-secondBg",
  },
  error: {
    icon: <AlertTriangle className="h-5 w-5" />,
    containerClasses: "border-2 border-danger-secondBg bg-danger-mainBg",
    color: "text-danger-primary",
    secondaryBG: "bg-danger-secondBg",
  },
  info: {
    icon: <InfoIcon className="h-5 w-5" />,
    containerClasses: "border-2 border-sea-secondBg bg-sea-mainBg",
    color: "text-sea-primary",
    secondaryBG: "bg-sea-secondBg",
  },
  warning: {
    icon: <AlertTriangle className="h-5 w-5" />,
    containerClasses: "border-2 border-solid border-warning-secondBg bg-warning-mainBg",
    color: "text-warning-primary",
    secondaryBG: "bg-warning-secondBg",
  },
}

const CustomToast: React.FC<CustomToastProps> = ({ type, title, message, className, closeToast }) => {
  const config = toastConfig[type]
  return (
    <div
      className={cn(
        "flex w-full flex-row items-center justify-between rounded-md p-2 py-2",
        config.containerClasses,
        className,
      )}
      role="alert"
      aria-live="polite"
    >
      <div className={cn("mr-4 rounded-sm p-[3px]", config.color, config.secondaryBG)}>{config.icon}</div>
      <div className="flex w-full flex-col">
        {title && <div className={cn("mb-1 text-base font-bold leading-5", config.color)}>{title}</div>}
        <div className={cn("text-base leading-5", config.color)}>{message}</div>
      </div>
      <CloseIcon className={cn("h-4 w-4 cursor-pointer", config.color)} onClick={closeToast} />
    </div>
  )
}

const defaultOptions: ToastOptions = {
  autoClose: 5000,
  closeOnClick: false,
  pauseOnHover: true,
  draggable: true,
  hideProgressBar: true,
  closeButton: false,
  position: "top-right",
  className: "p-1 m-0 h-fit bg-transparent [&>*]:p-0 [&>*]:m-0 shadow-none",
}

const Toast = ({ type, title, message, options = {} }: CustomToastProps) => {
  const mergedOptions = {
    ...defaultOptions,
    ...options,
    toastId: options.toastId || message, // Deduplicate identical messages
  }

  // Clear the waiting queue for errors so that previous errors don't keep popping up
  if (type === "error") {
    toast.clearWaitingQueue()
  }

  return toast(
    ({ closeToast }) => <CustomToast type={type} title={title} message={message} closeToast={closeToast} />,
    mergedOptions,
  )
}

export default Toast
